/*
 * Bottle PDP inline configurator.
 * It mirrors selections into the theme's existing touch-up controls so kit
 * variant resolution, line-item properties, pricing, and cart behavior remain
 * owned by the established Partify flow.
 */
(function () {
  'use strict';

  function boot() {
    var root = document.querySelector('[data-tubp-configurator]');
    if (!root || root.__tubpConfiguratorBooted) return;
    root.__tubpConfiguratorBooted = true;

    var content = root.querySelector('[data-tubp-configurator-content]');
    var packageImages = {
      paint: root.getAttribute('data-tubp-paint-image') || '',
      clearcoat: root.getAttribute('data-tubp-clearcoat-image') || '',
      primer: root.getAttribute('data-tubp-primer-image') || '',
    };
    var state = {
      method: 'vin',
      colorCode: '',
      colorDetails: null,
      selectedPackage: '',
      vin: '',
      plate: '',
      plateState: '',
      allowedCodes: null,
      allowedCodesLoading: false,
      paintDataAvailable: null,
      twoToneCodes: [],
      toneChoice: '',
      toneArea: '',
      manualOverride: false,
      vehicleKey: '',
      resolvingVin: false,
      resolvedVin: '',
      resolvingPlate: false,
      message: '',
      manualVinSaved: false,
      adding: false,
    };
    var paintContextRequest = 0;

    function normalize(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '');
    }

    function money(value) {
      var amount = Number(value);
      return isNaN(amount) ? '' : '$' + amount.toFixed(2);
    }

    function escapeHtml(value) {
      return String(value || '').replace(/[&<>"']/g, function (character) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
      });
    }

    function normalizePaintCode(value) {
      return String(value || '').trim().toUpperCase();
    }

    function comparablePaintCode(value) {
      var code = normalizePaintCode(value);
      return code.indexOf('WA') === 0 ? code.slice(2) : code;
    }

    function paintCodeMatches(code, allowed) {
      var comparable = comparablePaintCode(code);
      var approved = comparablePaintCode(allowed);
      return comparable === approved || comparable.indexOf(approved + ' ') === 0 || comparable.indexOf(approved + '+') === 0;
    }

    function uniquePaintCodes(codes) {
      return (Array.isArray(codes) ? codes : []).map(normalizePaintCode).filter(function (code, index, all) {
        return code && !isVinMatch(code) && all.indexOf(code) === index;
      });
    }

    function packageImageKeys(name) {
      var key = normalize(name);
      if (key === 'paintonly') return ['paint'];
      if (key === 'essential') return ['paint', 'clearcoat'];
      if (key === 'preferred' || key === 'complete') return ['primer', 'paint', 'clearcoat'];
      return ['paint'];
    }

    function canonicalPackageImage(name) {
      var map = getMap();
      var keys = Object.keys(map);
      var vinKey = keys.find(isVinMatch);
      var orderedKeys = vinKey
        ? [vinKey].concat(keys.filter(function (key) { return key !== vinKey; }))
        : keys;
      var targetPackage = normalize(name);

      for (var index = 0; index < orderedKeys.length; index += 1) {
        var entries = Array.isArray(map[orderedKeys[index]]) ? map[orderedKeys[index]] : [];
        var entry = entries.find(function (item) {
          return item &&
            String(item.size || '').toLowerCase() === 'bottle' &&
            normalize(item.packageName) === targetPackage &&
            item.productImage;
        });
        if (entry) return entry.productImage;
      }
      return '';
    }

    function packageMedia(name, entry) {
      // Keep package artwork stable while customers switch between VIN, plate,
      // and manual paint-code matching. Prefer the canonical VIN package image;
      // the individual component assets remain a last-resort loading fallback.
      var canonicalImage = canonicalPackageImage(name);
      var resolvedImage = canonicalImage || (entry && entry.productImage) || '';
      var sources = resolvedImage
        ? [resolvedImage]
        : packageImageKeys(name).map(function (key) { return packageImages[key]; }).filter(Boolean);
      return '<span class="tubp-package-choice__media tubp-package-choice__media--' + sources.length + '">' +
        sources.map(function (source, index) {
          return '<img src="' + escapeHtml(source) + '" alt="" loading="lazy" data-tubp-package-image="' + index + '">';
        }).join('') +
      '</span>';
    }

    function getMap() {
      return window.__touchupSizeMap || {};
    }

    function isVinMatch(code) {
      return /^(?:\.vin|vin)$|match\s+(?:paint\s+)?by\s+vin/i.test(String(code || '').trim());
    }

    function colorOption(code) {
      var select = document.getElementById('variant-selector');
      if (!select) return null;
      return Array.prototype.slice.call(select.options).find(function (option) {
        var title = option.getAttribute('data-variant-title') || option.textContent || '';
        return String(title).trim().toUpperCase() === String(code).trim().toUpperCase();
      }) || null;
    }

    function colorLabel(code) {
      var colors = window.PartifyTouchupColorDetails;
      if (colors && activeVehicle()) return colors.label(state.colorDetails, code);
      var option = colorOption(code);
      var label = option ? String(option.textContent || '').split('+')[0].trim() : '';
      return label && label !== code ? code + ' · ' + label : code;
    }

    function mapCodeKey(code) {
      var target = normalizePaintCode(code);
      return Object.keys(getMap()).find(function (key) {
        return normalizePaintCode(key) === target;
      }) || '';
    }

    function codeForMethod() {
      if (state.method !== 'vin') return mapCodeKey(state.colorCode) || state.colorCode;
      var exactCode = resolvedVehiclePaintCode();
      if (exactCode) return exactCode;
      return Object.keys(getMap()).find(isVinMatch) || '';
    }

    function bottleEntries() {
      var code = codeForMethod();
      return (getMap()[code] || []).filter(function (entry) {
        return entry && entry.id && String(entry.size || '').toLowerCase() === 'bottle';
      });
    }

    function packageMeta(name) {
      var key = normalize(name);
      var meta = {
        paintonly: {
          description: 'Factory-matched paint only.',
          inclusions: ['Color bottle', 'Use when you already have prep and clearcoat'],
          note: 'Clearcoat not included',
        },
        essential: {
          description: 'The balanced system for most chips and surface scratches.',
          inclusions: ['Color bottle', 'Clearcoat bottle'],
          note: 'Recommended for a sealed finish',
        },
        preferred: {
          description: 'More preparation for scratches that break through the finish.',
          inclusions: ['Primer', 'Color bottle', 'Clearcoat bottle'],
          note: 'For exposed primer or metal',
        },
        complete: {
          description: 'The full repair system for a more involved touch-up.',
          inclusions: ['Prep supplies', 'Primer', 'Color bottle', 'Clearcoat bottle'],
          note: 'Full repair system',
        },
      };
      return meta[key] || {
        description: 'A vehicle-matched bottle package.',
        inclusions: ['Factory-matched color'],
        note: '',
      };
    }

    function entryForPackage(name) {
      return bottleEntries().find(function (entry) {
        return String(entry.packageName || '') === String(name || '');
      }) || null;
    }

    function uniquePackages() {
      var order = ['Paint Only', 'Essential', 'Preferred', 'Complete'];
      var packages = bottleEntries().map(function (entry) { return entry.packageName; });
      var unique = packages.filter(function (item, index) { return item && packages.indexOf(item) === index; });
      return unique.sort(function (a, b) {
        var aIndex = order.map(normalize).indexOf(normalize(a));
        var bIndex = order.map(normalize).indexOf(normalize(b));
        return (aIndex < 0 ? 999 : aIndex) - (bIndex < 0 ? 999 : bIndex);
      });
    }

    function selectedPackage() {
      var packages = uniquePackages();
      if (packages.indexOf(state.selectedPackage) !== -1) return state.selectedPackage;
      return packages.find(function (item) { return normalize(item) === 'essential'; }) || packages[0] || '';
    }

    function activeVehicle() {
      try {
        var list = typeof window.getSearchTerms === 'function'
          ? window.getSearchTerms()
          : JSON.parse(localStorage.getItem('searchTerms') || '[]');
        return Array.isArray(list) ? list[0] : null;
      } catch (error) {
        return null;
      }
    }

    function vehicleIdentity(vehicle) {
      if (!vehicle) return '';
      return normalize([
        vehicle.year,
        vehicle.make,
        vehicle.model,
        vehicle.submodel || vehicle.trim,
        vehicle.engine,
        vehicle.vin,
        vehicle.plate,
      ].filter(Boolean).join(' ') || vehicle.ymm || vehicle.YMM || vehicle.vehicle);
    }

    function vehicleLabel(vehicle) {
      if (!vehicle) return '';
      return String(
        vehicle.ymm ||
        vehicle.YMM ||
        vehicle.vehicle ||
        [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel].filter(Boolean).join(' ')
      ).trim();
    }

    function localTwoToneCodes(vehicle) {
      var config = window.__PARTIFY_TWO_TONE_PAINT_RULES;
      var resolver = window.PartifyTwoToneRuleResolver;
      if (!config || !Array.isArray(config.rules) || !resolver || typeof resolver.selectRule !== 'function') return [];
      var selected = resolver.selectRule(config.rules, vehicle || {});
      return selected && Array.isArray(selected.codes) ? selected.codes : [];
    }

    function loadVehiclePaintContext(vehicle) {
      var request = ++paintContextRequest;
      state.colorDetails = null;
      if (!vehicle) {
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = null;
        state.twoToneCodes = [];
        state.toneChoice = '';
        state.toneArea = '';
        render();
        return Promise.resolve();
      }

      state.allowedCodes = null;
      state.allowedCodesLoading = true;
      state.paintDataAvailable = null;
      render();
      var finderData = window.PartifyTouchupFinderData || {};
      if (typeof finderData.getColorDetails === 'function') {
        Promise.resolve(finderData.getColorDetails(vehicle)).then(function (details) {
          if (request !== paintContextRequest) return;
          state.colorDetails = details;
          var colors = window.PartifyTouchupColorDetails;
          if (colors) {
            colors.updateOptions(content.querySelector('[data-tubp-color]'), details);
            colors.renderPreview(content.querySelector('[data-touchup-color-preview]'), details, state.colorCode);
          }
        }).catch(function () {});
      }
      var kitPromise = typeof finderData.ensureKitProducts === 'function'
        ? Promise.resolve(finderData.ensureKitProducts(vehicle)).catch(function () { return false; })
        : Promise.resolve(false);
      var allowedPromise = typeof finderData.getAllowedCodes === 'function'
        ? finderData.getAllowedCodes(vehicle)
        : typeof window.__partifyTouchupFetchAllowedCodes === 'function'
          ? window.__partifyTouchupFetchAllowedCodes(vehicle)
          : Promise.resolve(null);
      var twoTonePromise = typeof finderData.getTwoToneCodes === 'function'
        ? finderData.getTwoToneCodes(vehicle)
        : Promise.resolve(localTwoToneCodes(vehicle));

      return kitPromise.then(function () {
        return Promise.all([allowedPromise, twoTonePromise]);
      }).then(function (results) {
        if (request !== paintContextRequest) return;
        state.paintDataAvailable = Array.isArray(results[0]) && (results[0].partifyVehiclePaintDataAvailable === true || results[0].length > 0);
        state.allowedCodes = Array.isArray(results[0]) ? uniquePaintCodes(results[0]) : null;
        state.allowedCodesLoading = false;
        state.twoToneCodes = uniquePaintCodes(results[1]);
        if (!state.twoToneCodes.length) {
          state.toneChoice = '';
          state.toneArea = '';
        }
        if (state.method === 'manual' && state.colorCode && manualCodes().indexOf(state.colorCode) === -1) {
          state.colorCode = '';
          state.selectedPackage = '';
        }
        render();
      }).catch(function () {
        if (request !== paintContextRequest) return;
        state.allowedCodes = null;
        state.allowedCodesLoading = false;
        state.paintDataAvailable = false;
        state.twoToneCodes = uniquePaintCodes(localTwoToneCodes(vehicle));
        render();
      });
    }

    function twoToneEligible() {
      return state.twoToneCodes.length > 0;
    }

    function secondaryToneSelected() {
      return twoToneEligible() && state.toneChoice === 'two' && state.toneArea === 'secondary';
    }

    function toneSelectionReady() {
      if (!twoToneEligible()) return true;
      if (state.toneChoice === 'one') return true;
      return state.toneChoice === 'two' && (state.toneArea === 'primary' || state.toneArea === 'secondary');
    }

    function activeVinString() {
      var vehicle = activeVehicle() || {};
      var manualInput = root.querySelector('[data-tubp-manual-vin]');
      return String(
        vehicle.vin || vehicle.VIN || vehicle.vehicleVin || vehicle.vehicleVIN ||
        vehicle.vinNumber || vehicle.vin_string || vehicle.vinString ||
        vehicle.decodedVin || vehicle.decodedVIN || (manualInput && manualInput.value) || state.vin || ''
      ).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOQ]/g, '').slice(0, 17);
    }

    function syncManualVinProperty() {
      var vin = activeVinString();
      Array.prototype.slice.call(document.querySelectorAll('#product-form, form.product-form, form[action*="/cart/add"]')).forEach(function (form) {
        if (!form || vin.length !== 17) return;
        var input = form.querySelector('input[name="properties[VIN]"]') || getOrCreateHiddenInput(form, 'input[name="properties[VIN]"]', 'properties[VIN]');
        input.value = vin;
      });
    }

    function resolvedVehiclePaintCode() {
      var vehicle = activeVehicle() || {};
      var finderData = window.PartifyTouchupFinderData || {};
      var raw = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle)
        : vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format || '';
      raw = String(raw || '').trim();
      if (raw.indexOf(' / ') !== -1) raw = raw.split(' / ').pop().trim();
      if (!raw || isVinMatch(raw) || normalizePaintCode(raw) === activeVinString()) return '';
      return mapCodeKey(raw);
    }

    function vinPaintDataAvailable() {
      return state.paintDataAvailable === true;
    }

    function vinAvailabilityStatus() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin) return '';
      if (state.allowedCodesLoading) return 'Checking Shopify paint availability for this vehicle…';
      if (!vinPaintDataAvailable()) {
        return 'Touch-up paint is not available for this vehicle because we do not have a Shopify paint-code mapping for it. Match by VIN and Add to Cart are disabled.';
      }
      return '';
    }

    function vinResultMarkup() {
      if (state.method !== 'vin' || state.vin.length !== 17 || state.resolvedVin !== state.vin || state.allowedCodesLoading || !vinPaintDataAvailable()) return '';
      var code = codeForMethod();
      if (!code) return '';
      var label = isVinMatch(code) ? 'Match by VIN' : code;
      return '<p class="tubp-inline-configurator__vin-result" data-tubp-vin-result role="status"><span>VIN: ' + escapeHtml(activeVinString()) + '</span><span>Paint code: ' + escapeHtml(label) + '</span></p>';
    }

    function vinDecodeSuccessMessage(vehicle) {
      var finderData = window.PartifyTouchupFinderData || {};
      var code = typeof finderData.getResolvedPaintCode === 'function'
        ? finderData.getResolvedPaintCode(vehicle || activeVehicle())
        : (vehicle && (vehicle.paintCode || vehicle.paint_code || vehicle.troublesome_format)) || '';
      code = String(code || '').trim();
      if (code && !isVinMatch(code)) {
        return 'VIN decoded successfully. Your vehicle was added to Garage. Paint code ' + code.toUpperCase() + ' was found.';
      }
      return 'VIN decoded successfully. Your vehicle was added to Garage. We will match the paint by VIN.';
    }

    function manualCodes() {
      var codes = Object.keys(getMap()).filter(function (code) { return !isVinMatch(code); }).sort();
      var filter = secondaryToneSelected() ? state.twoToneCodes : state.allowedCodes;
      if (!Array.isArray(filter)) return codes;
      return codes.filter(function (code) {
        return filter.some(function (allowed) { return paintCodeMatches(code, allowed); });
      });
    }

    function openGarage() {
      if (window.GarageV2 && typeof window.GarageV2.open === 'function') window.GarageV2.open();
      else if (typeof window.toggleGaragePopup === 'function') window.toggleGaragePopup();
      else {
        var sharedGarageButton = document.getElementById('easyBtn') || document.getElementById('easyBtnMobile');
        if (sharedGarageButton && typeof sharedGarageButton.click === 'function') sharedGarageButton.click();
      }
    }

    function garageMarkup(vehicle) {
      if (!vehicle) {
        return '<button type="button" class="tubp-inline-configurator__garage tubp-inline-configurator__garage--empty" data-tubp-garage>' +
          '<span class="tubp-inline-configurator__garage-icon" aria-hidden="true">+</span>' +
          '<span><strong>Add a vehicle to your garage</strong><small>Choose year, make and model once, then keep it for future repairs.</small></span>' +
          '<span aria-hidden="true">&rarr;</span>' +
        '</button>';
      }
      var label = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || vehicle.ymm || 'Saved vehicle';
      return '<div class="tubp-inline-configurator__garage is-saved">' +
        '<span class="tubp-inline-configurator__garage-icon" aria-hidden="true">&#10003;</span>' +
        '<span><small>Shopping for</small><strong>' + escapeHtml(label) + '</strong></span>' +
        '<button type="button" data-tubp-garage>Change</button>' +
      '</div>';
    }

    function syncExistingControls() {
      var code = codeForMethod();
      var activeVin = activeVinString();
      var matchByVinRequired = state.method === 'vin' && isVinMatch(code);
      var entry = entryForPackage(selectedPackage());
      if (!entry || !code) return false;

      var select = document.getElementById('variant-selector');
      var option = colorOption(code);
      if (select && option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      window.setTimeout(function () {
        ['touchup-size-buttons', 'touchup-package-buttons'].forEach(function (id, index) {
          var target = index === 0 ? 'Bottle' : entry.packageName;
          var button = Array.prototype.slice.call(document.querySelectorAll('#' + id + ' .touchup-option-button'))
            .find(function (item) { return String(item.textContent || '').trim() === String(target); });
          if (button && !button.disabled) button.click();
        });
      }, 0);

      window.__touchupSelectedKitVariantId = String(entry.id);
      var form = document.getElementById('product-form');
      if (form) {
        var vinInput = form.querySelector('input[name="properties[VIN]"]');
        if (activeVin.length === 17) {
          if (!vinInput) {
            vinInput = document.createElement('input');
            vinInput.type = 'hidden';
            vinInput.name = 'properties[VIN]';
            form.appendChild(vinInput);
          }
          vinInput.value = activeVin;
        } else {
          if (vinInput) vinInput.value = '';
        }
        var kitInput = form.querySelector('input[data-touchup-kit-variant-id]');
        if (!kitInput) {
          kitInput = document.createElement('input');
          kitInput.type = 'hidden';
          kitInput.name = 'id';
          kitInput.setAttribute('data-touchup-kit-variant-id', 'true');
          form.appendChild(kitInput);
        }
        kitInput.value = String(entry.id);
      }
      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = matchByVinRequired && activeVin.length === 17;
      window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = state.method === 'manual';
      return true;
    }

    function clearVinMatchState() {
      window.__PARTIFY_TOUCHUP_MATCH_BY_VIN_REQUIRED = false;
      window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = true;
      var form = document.getElementById('product-form');
      var vinInput = form && form.querySelector('input[name="properties[VIN]"]');
      if (vinInput) vinInput.value = activeVinString();
    }

    function setMethod(method) {
      if (secondaryToneSelected() && method !== 'manual') method = 'manual';
      state.method = method;
      state.manualOverride = method === 'manual';
      if (method !== 'manual') state.colorCode = '';
      if (method === 'manual') clearVinMatchState();
      else window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
      state.selectedPackage = '';
      state.message = '';
      render();
    }

    function renderToneFields() {
      if (!twoToneEligible()) return '';
      var choices = [
        { value: 'one', title: 'One factory color', copy: 'Use VIN, plate, or the vehicle\'s factory paint-code list.' },
        { value: 'two', title: 'Two factory colors', copy: 'Choose the main or accent color you need to repair.' },
      ];
      var markup = '<fieldset class="tubp-tone-choice"><legend>How many factory paint colors does your vehicle have?</legend><div class="tubp-tone-choice__grid">' + choices.map(function (choice) {
        var selected = state.toneChoice === choice.value;
        return '<button type="button" class="tubp-tone-option' + (selected ? ' is-selected' : '') + '" data-tubp-tone="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
      }).join('') + '</div></fieldset>';
      if (state.toneChoice === 'two') {
        markup += '<fieldset class="tubp-tone-choice"><legend>Which color are you touching up?</legend><div class="tubp-tone-choice__grid">' + [
          { value: 'primary', title: 'Main / primary color', copy: 'The color covering most of the vehicle.' },
          { value: 'secondary', title: 'Accent / secondary color', copy: 'The contrasting second factory color.' },
        ].map(function (choice) {
          var selected = state.toneArea === choice.value;
          return '<button type="button" class="tubp-tone-option' + (selected ? ' is-selected' : '') + '" data-tubp-tone-area="' + choice.value + '" aria-pressed="' + String(selected) + '"><strong>' + choice.title + '</strong><span>' + choice.copy + '</span></button>';
        }).join('') + '</div></fieldset>';
      }
      return markup;
    }

    function renderMethodFields() {
      if (state.method === 'vin') {
        return '<label class="tubp-inline-configurator__field"><span>Vehicle identification number</span><input data-tubp-vin value="' + state.vin + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="17-character VIN"></label>';
      }
      if (state.method === 'plate') {
        var states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
        var plateReady = state.plate.length >= 2 && !!state.plateState;
        return '<div class="tubp-inline-configurator__field-row"><label class="tubp-inline-configurator__field"><span>License plate</span><input data-tubp-plate value="' + escapeHtml(state.plate) + '" autocomplete="off" placeholder="ABC1234"></label><label class="tubp-inline-configurator__field"><span>State</span><select data-tubp-plate-state><option value="">State</option>' + states.map(function (code) { return '<option value="' + code + '"' + (state.plateState === code ? ' selected' : '') + '>' + code + '</option>'; }).join('') + '</select></label></div>' +
          '<button type="button" class="tubp-inline-configurator__resolve" data-tubp-resolve-plate' + (!plateReady || state.resolvingPlate ? ' disabled' : '') + '>' + (state.resolvingPlate ? 'Finding your factory color…' : 'Find my factory color') + '</button>' +
          '<p class="tubp-inline-configurator__helper" role="status">' + escapeHtml(state.message || 'We use your plate and state to find the VIN, then match the factory color.') + '</p>';
      }
      var codes = manualCodes();
      var placeholder = state.allowedCodesLoading ? 'Loading paint codes…' : codes.length ? 'Select factory paint code' : 'No paint codes found for this vehicle';
      var options = ['<option value="">' + placeholder + '</option>'].concat(codes.map(function (code) {
        return '<option value="' + code.replace(/"/g, '&quot;') + '"' + (code === state.colorCode ? ' selected' : '') + '>' + escapeHtml(colorLabel(code)) + '</option>';
      }));
      return '<label class="tubp-inline-configurator__field"><span>Factory paint code</span><select data-tubp-color' + (state.allowedCodesLoading || !codes.length ? ' disabled' : '') + '>' + options.join('') + '</select></label>' +
        '<figure class="touchup-color-preview" data-touchup-color-preview hidden></figure>' +
        '<p class="tubp-inline-configurator__helper">' + (secondaryToneSelected() ? 'Choose the factory code for the accent / secondary color you are repairing.' : 'Paint codes are limited to the selected vehicle. Find yours on the driver-side door jamb.') + '</p>' +
        (state.colorCode ? manualVinRecommendationMarkup() : '');
    }

    function manualVinRecommendationMarkup() {
      var vin = activeVinString();
      return '<div class="tubp-inline-configurator__manual-vin" data-tubp-manual-vin-prompt>' +
        '<strong>Add VIN for Better Color Match <em>(Recommended)</em></strong>' +
        '<label class="tubp-inline-configurator__field"><span>Vehicle Identification Number</span>' +
          '<span class="tubp-inline-configurator__manual-vin-row"><input data-tubp-manual-vin name="properties[VIN]" value="' + escapeHtml(vin) + '" maxlength="17" inputmode="text" autocomplete="off" placeholder="e.g. 18UYA31581L000000"></span></label>' +
        '<p class="tubp-inline-configurator__helper">Enter all 17 characters. Your selected paint code stays in place, and the VIN is automatically sent with the order so our paint team can refine the color match.</p>' +
        (state.manualVinSaved ? '<p class="tubp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>' : '') +
      '</div>';
    }

    function renderPackageCards(isColorReady) {
      if (!isColorReady) {
        return '<div class="tubp-inline-configurator__packages" aria-label="Bottle packages available after color matching">' +
          [
            { name: 'Paint Only', note: 'Color bottle only' },
            { name: 'Essential', note: 'Paint + clearcoat', recommended: true },
            { name: 'Preferred', note: 'Primer + paint + clearcoat' },
          ].map(function (item) {
            var meta = packageMeta(item.name);
            return '<button type="button" class="tubp-package-choice" disabled aria-disabled="true">' +
              packageMedia(item.name, null) +
              '<span class="tubp-package-choice__copy">' +
              '<span class="tubp-package-choice__topline"><span>' + item.name + '</span>' + (item.recommended ? '<span class="tubp-package-choice__recommendation">Recommended</span>' : '') + '</span>' +
              '<span class="tubp-package-choice__description">' + meta.description + '</span>' +
              '<span class="tubp-package-choice__includes">' + meta.inclusions.map(function (inclusion) { return '<span>' + inclusion + '</span>'; }).join('') + '</span>' +
              '<span class="tubp-package-choice__footer"><span>' + meta.note + '</span></span>' +
              '</span>' +
            '</button>';
          }).join('') +
        '</div><p class="tubp-inline-configurator__helper">Match your vehicle or select a paint code to unlock package pricing.</p>';
      }
      var packageList = uniquePackages();
      if (!packageList.length) {
        return '<p class="tubp-inline-configurator__empty">Choose a color first to see the available bottle packages.</p>';
      }
      state.selectedPackage = selectedPackage();
      return '<div class="tubp-inline-configurator__packages">' + packageList.map(function (name) {
        var entry = entryForPackage(name);
        var meta = packageMeta(name);
        var selected = name === state.selectedPackage;
        var recommended = normalize(name) === 'essential';
        return '<button type="button" class="tubp-package-choice' + (selected ? ' is-selected' : '') + '" data-tubp-package="' + name.replace(/"/g, '&quot;') + '" aria-pressed="' + String(selected) + '">' +
          packageMedia(name, entry) +
          '<span class="tubp-package-choice__copy">' +
          '<span class="tubp-package-choice__topline"><span>' + name + '</span>' + (recommended ? '<span class="tubp-package-choice__recommendation">Recommended</span>' : '') + '</span>' +
          '<span class="tubp-package-choice__description">' + meta.description + '</span>' +
          '<span class="tubp-package-choice__includes">' + meta.inclusions.map(function (item) { return '<span>' + item + '</span>'; }).join('') + '</span>' +
          '<span class="tubp-package-choice__footer"><span>' + meta.note + '</span><strong>' + money(entry && entry.price) + '</strong></span>' +
          '</span>' +
        '</button>';
      }).join('') + '</div>';
    }

    function render() {
      if (!content) return;
      var previousPreview = content.querySelector('[data-touchup-color-preview]');
      var vehicle = activeVehicle();
      var toneReady = toneSelectionReady();
      var methodReady = toneReady && (state.method === 'vin'
        ? state.vin.length === 17 && state.resolvedVin === state.vin && !state.resolvingVin && !state.allowedCodesLoading && vinPaintDataAvailable()
        : state.method === 'plate'
          ? false
          : !!state.colorCode);
      var packageReady = !!selectedPackage();
      content.innerHTML =
        garageMarkup(vehicle) +
        '<div class="tubp-inline-configurator__step">' +
          '<div class="tubp-inline-configurator__step-heading"><span>01</span><div><h3>Find your factory color</h3><p>VIN is the most accurate option.</p></div></div>' +
          renderToneFields() +
          '<div class="tubp-inline-configurator__methods" role="radiogroup" aria-label="Choose a color matching method">' +
            ['vin', 'plate', 'manual'].map(function (method) {
              var labels = { vin: ['VIN match', 'Most accurate'], plate: ['License plate', 'VIN lookup'], manual: ['Paint code', 'I know it'] };
              var disabled = !toneReady || (secondaryToneSelected() && method !== 'manual');
              return '<button type="button" class="tubp-match-method' + (state.method === method ? ' is-selected' : '') + '" data-tubp-method="' + method + '" role="radio" aria-checked="' + String(state.method === method) + '"' + (disabled ? ' disabled' : '') + '><strong>' + labels[method][0] + '</strong><span>' + labels[method][1] + '</span></button>';
            }).join('') +
          '</div>' +
          (toneReady ? renderMethodFields() : '<p class="tubp-inline-configurator__helper">Choose the factory-color setup above to continue.</p>') +
          vinResultMarkup() +
          (state.method === 'vin' && state.message ? '<p class="tubp-inline-configurator__helper" role="status">' + escapeHtml(state.message) + '</p>' : '') +
          (vinAvailabilityStatus() ? '<p class="tubp-inline-configurator__helper" role="status">' + escapeHtml(vinAvailabilityStatus()) + '</p>' : '') +
        '</div>' +
        '<div class="tubp-inline-configurator__step' + (methodReady ? '' : ' is-disabled') + '">' +
          '<div class="tubp-inline-configurator__step-heading"><span>02</span><div><h3>Choose your bottle package</h3><p>Clearcoat protects the finished repair. Add primer only when the damage exposes metal or primer.</p></div></div>' +
          renderPackageCards(methodReady) +
        '</div>' +
        '<div class="tubp-inline-configurator__cta-row">' +
          '<div><span class="tubp-inline-configurator__selection-label">Your selection</span><strong>' + (methodReady && packageReady ? (state.method === 'manual' ? colorLabel(state.colorCode) : 'Factory match') + ' · ' + state.selectedPackage + ' bottle' : 'Complete your bottle setup') + '</strong></div>' +
          '<button type="button" class="tubp-inline-configurator__atc" data-tubp-add' + (methodReady && packageReady && !state.adding ? '' : ' disabled') + '>' + (state.adding ? 'Adding…' : 'Add to cart') + ' <span aria-hidden="true">→</span></button>' +
        '</div>';

      var colors = window.PartifyTouchupColorDetails;
      var preview = content.querySelector('[data-touchup-color-preview]');
      if (previousPreview && preview) {
        preview.replaceWith(previousPreview);
        preview = previousPreview;
      }
      if (colors) colors.renderPreview(preview, state.colorDetails, state.colorCode);
      bind();
    }

    function matchViaVin() {
      var code = codeForMethod();
      if (!code) return false;
      state.colorCode = code;
      return true;
    }

    function syncVinToGarage() {
      var vin = state.vin;
      if (vin.length !== 17) return Promise.resolve(false);
      if (state.resolvedVin === vin) return Promise.resolve(true);

      var sync = window.PartifyTouchupVinGarageSync;
      if (!sync || typeof sync.decodeAndActivate !== 'function') {
        state.resolvedVin = vin;
        state.resolvingVin = false;
        state.message = '';
        render();
        return Promise.resolve(true);
      }

      state.resolvingVin = true;
      state.message = 'Decoding VIN and updating your Garage\u2026';
      render();
      return Promise.resolve(sync.decodeAndActivate(vin)).then(function (vehicle) {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = vin;
        state.message = vinDecodeSuccessMessage(vehicle);
        return loadVehiclePaintContext(vehicle || activeVehicle()).then(function () {
          if (state.vin !== vin) return false;
          render();
          return true;
        });
      }).catch(function () {
        if (state.vin !== vin) return false;
        state.resolvingVin = false;
        state.resolvedVin = '';
        state.message = 'We could not decode that VIN. Check it and try again.';
        render();
        return false;
      });
    }

    function cartProperties(form, entry) {
      var properties = {};
      if (form) {
        new FormData(form).forEach(function (value, key) {
          var match = key.match(/^properties\[(.+)\]$/);
          if (match && value != null && String(value).length) properties[match[1]] = value;
        });
      }
      var resolvedCode = codeForMethod();
      properties['Touch-Up Color'] = resolvedCode && !isVinMatch(resolvedCode) ? resolvedCode : 'Match by VIN';
      properties['Kit Format'] = entry.size || 'Bottle';
      properties['Kit Package'] = entry.packageName || selectedPackage();
      properties['Kit Product'] = entry.productTitle || document.querySelector('h1')?.textContent?.trim() || document.title;
      var activeVehicleLabel = vehicleLabel(activeVehicle());
      if (activeVehicleLabel) properties.Vehicle = activeVehicleLabel;
      else delete properties.Vehicle;
      delete properties.EasySearch;
      delete properties.Fits;
      var activeVin = activeVinString();
      if (activeVin.length === 17) properties.VIN = activeVin;
      else delete properties.VIN;
      return properties;
    }

    function confirmQuantity(payload, properties) {
      var confirmation = window.PartifyCartQuantityConfirmation;
      if (!confirmation || typeof confirmation.confirmAdd !== 'function') return Promise.resolve(true);
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : { items: [] }; })
        .catch(function () { return { items: [] }; })
        .then(function (cart) {
          return confirmation.confirmAdd({
            cart: cart,
            items: [{
              id: payload.id,
              variant_id: payload.id,
              quantity: 1,
              title: properties['Kit Product'],
              properties: properties,
              partifyQuantityRuleKey: 'touch_up_paint_kit',
            }],
          }).then(function (result) { return result.confirmed !== false; });
        });
    }

    function submitNativeForm() {
      if (state.adding) return;
      if (state.method === 'vin' && (state.allowedCodesLoading || !vinPaintDataAvailable())) {
        state.message = vinAvailabilityStatus() || 'We could not verify Shopify paint data for this vehicle. Match by VIN and Add to Cart are disabled.';
        render();
        return;
      }
      var entry = entryForPackage(selectedPackage());
      if (!entry || !entry.id) return;
      state.adding = true;
      if (!syncExistingControls()) {
        state.adding = false;
        return;
      }
      var form = document.getElementById('product-form') || document.querySelector('form.product-form, form[action*="/cart/add"]');
      var properties = cartProperties(form, entry);
      var payload = { id: Number(entry.id), quantity: 1, properties: properties };
      var button = content.querySelector('[data-tubp-add]');
      var idleLabel = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding\u2026';
      }

      confirmQuantity(payload, properties)
        .then(function (confirmed) {
          if (!confirmed) {
            state.adding = false;
            render();
            return null;
          }
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
        })
        .then(function (response) {
          if (response === null) return;
          if (!response.ok) throw new Error('Unable to add this bottle package to the cart.');
          if (window.__partifyAddOnStyles?.afterAdd({ items: [payload], onClose: function () { state.adding = false; render(); } })) return;
          window.location.assign('/cart');
        })
        .catch(function (error) {
          state.adding = false;
          state.message = error.message || 'We could not add this package. Please try again.';
          if (button) {
            button.disabled = false;
            button.innerHTML = idleLabel;
          }
          window.alert(state.message);
        });
    }

    function addToCart() {
      if (state.method === 'vin' && state.resolvedVin !== state.vin) {
        syncVinToGarage().then(function (ready) { if (ready) addToCart(); });
        return;
      }
      if (state.method === 'vin' && !matchViaVin()) return;
      if (state.method === 'plate') return;
      submitNativeForm();
    }

    function resolvePlate() {
      if (state.resolvingPlate || state.plate.length < 2 || !state.plateState) return;
      state.resolvingPlate = true;
      state.message = 'Looking up your vehicle and factory color…';
      render();
      if (typeof window.fetchBumpersLicenseToVin !== 'function') {
        state.resolvingPlate = false;
        state.message = 'Plate lookup is unavailable right now. Please use your VIN or paint code.';
        render();
        return;
      }
      window.fetchBumpersLicenseToVin(state.plateState, state.plate)
        .then(function (result) {
          var vin = String((result && result.vin) || '').trim().toUpperCase();
          if (vin.length !== 17) throw new Error('No VIN found');
          state.method = 'vin';
          state.vin = vin;
          state.resolvedVin = '';
          state.manualOverride = false;
          window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
          state.resolvingPlate = false;
          state.message = '';
          state.selectedPackage = '';
          syncVinToGarage();
        })
        .catch(function () {
          state.resolvingPlate = false;
          state.message = 'We could not find that plate. Check the plate and state, or use your VIN or paint code.';
          render();
        });
    }

    function captureManualVin(manualVin) {
      state.vin = manualVin.value.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOQ]/g, '').slice(0, 17);
      state.manualVinSaved = false;
      manualVin.value = state.vin;
      var submit = content.querySelector('[data-tubp-manual-vin-submit]');
      var prompt = content.querySelector('[data-tubp-manual-vin-prompt]');
      var success = prompt && prompt.querySelector('.tubp-inline-configurator__manual-vin-success');
      if (state.vin.length === 17) {
        state.manualVinSaved = true;
        syncManualVinProperty();
        if (submit) { submit.textContent = 'VIN added'; submit.disabled = true; }
        if (prompt && !success) prompt.insertAdjacentHTML('beforeend', '<p class="tubp-inline-configurator__manual-vin-success" role="status">VIN added to this color match.</p>');
      } else {
        if (submit) { submit.textContent = 'Add VIN'; submit.disabled = true; }
        if (success) success.remove();
      }
    }

    document.addEventListener('input', function (event) {
      var manualVin = event.target;
      if (!manualVin || !manualVin.matches || !manualVin.matches('[data-tubp-manual-vin]') || !root.contains(manualVin)) return;
      captureManualVin(manualVin);
    }, true);

    function bind() {
      Array.prototype.slice.call(content.querySelectorAll('[data-tubp-method]')).forEach(function (button) {
        button.addEventListener('click', function () { setMethod(button.getAttribute('data-tubp-method')); });
      });
      Array.prototype.slice.call(content.querySelectorAll('[data-tubp-package]')).forEach(function (button) {
        button.addEventListener('click', function () { state.selectedPackage = button.getAttribute('data-tubp-package'); syncExistingControls(); render(); });
      });
      Array.prototype.slice.call(content.querySelectorAll('[data-tubp-tone]')).forEach(function (button) {
        button.addEventListener('click', function () {
          state.toneChoice = button.getAttribute('data-tubp-tone');
          state.toneArea = '';
          state.colorCode = '';
          state.selectedPackage = '';
          if (state.toneChoice === 'two') clearVinMatchState();
          render();
        });
      });
      Array.prototype.slice.call(content.querySelectorAll('[data-tubp-tone-area]')).forEach(function (button) {
        button.addEventListener('click', function () {
          state.toneArea = button.getAttribute('data-tubp-tone-area');
          state.colorCode = '';
          state.selectedPackage = '';
          if (state.toneArea === 'secondary') {
            state.method = 'manual';
            state.manualOverride = true;
            clearVinMatchState();
          } else {
            state.method = 'vin';
            state.manualOverride = false;
            window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
          }
          render();
        });
      });
      var color = content.querySelector('[data-tubp-color]');
      if (color) color.addEventListener('change', function () {
        state.manualOverride = true;
        state.colorCode = color.value;
        state.manualVinSaved = false;
        clearVinMatchState();
        render();
      });
      var vin = content.querySelector('[data-tubp-vin]');
      if (vin) vin.addEventListener('input', function () {
        var wasReady = state.vin.length === 17;
        state.manualOverride = false;
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
        state.vin = vin.value.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOQ]/g, '').slice(0, 17);
        state.resolvedVin = '';
        state.message = '';
        vin.value = state.vin;
        if (state.vin.length === 17) syncVinToGarage();
        else if (wasReady) render();
      });
      var plate = content.querySelector('[data-tubp-plate]');
      if (plate) plate.addEventListener('input', function () {
        state.message = '';
        state.plate = plate.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        plate.value = state.plate;
        var resolveButton = content.querySelector('[data-tubp-resolve-plate]');
        if (resolveButton) resolveButton.disabled = state.plate.length < 2 || !state.plateState || state.resolvingPlate;
      });
      var plateState = content.querySelector('[data-tubp-plate-state]');
      if (plateState) plateState.addEventListener('change', function () { state.message = ''; state.plateState = plateState.value; render(); });
      var resolvePlateButton = content.querySelector('[data-tubp-resolve-plate]');
      if (resolvePlateButton) resolvePlateButton.addEventListener('click', resolvePlate);
      var add = content.querySelector('[data-tubp-add]');
      if (add) add.addEventListener('click', addToCart);
      Array.prototype.slice.call(content.querySelectorAll('[data-tubp-garage]')).forEach(function (button) {
        button.addEventListener('click', openGarage);
      });
    }

    function waitForData(attempt) {
      if (Object.keys(getMap()).length) {
        syncVehicleState(activeVehicle());
        return;
      }
      if (attempt > 30) {
        content.innerHTML = '<p class="tubp-inline-configurator__empty">Factory-color options are still loading. Refresh the page, then try again.</p>';
        return;
      }
      window.setTimeout(function () { waitForData(attempt + 1); }, 250);
    }

    waitForData(0);

    var vehicleRenderTimer = 0;
    function syncVehicleState(vehicle) {
      var nextKey = vehicleIdentity(vehicle);
      var vehicleChanged = nextKey !== state.vehicleKey;
      if (vehicleChanged) {
        state.vehicleKey = nextKey;
        state.manualOverride = false;
        window.__PARTIFY_TOUCHUP_MANUAL_PAINT_OVERRIDE = false;
        state.toneChoice = '';
        state.toneArea = '';
        state.colorCode = '';
        state.selectedPackage = '';
      }

      if (!state.manualOverride) {
        var vehicleVin = String((vehicle && vehicle.vin) || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOQ]/g, '');
        var vehicleCode = normalizePaintCode(vehicle && (vehicle.paintCode || vehicle.paint_code));
        if (vehicleVin.length === 17) {
          state.method = 'vin';
          state.vin = vehicleVin;
          state.resolvedVin = vehicleVin;
        } else if (vehicleCode) {
          state.method = 'manual';
          state.colorCode = vehicleCode;
        }
      }

      render();
      loadVehiclePaintContext(vehicle);
    }

    function refreshFromGarage() {
      window.clearTimeout(vehicleRenderTimer);
      vehicleRenderTimer = window.setTimeout(function () {
        syncVehicleState(activeVehicle());
      }, 120);
    }
    window.addEventListener('partify:vehicle:changed', refreshFromGarage);
    window.addEventListener('searchTermsUpdated', refreshFromGarage);
    document.addEventListener('variantsUpdated', function () { window.setTimeout(render, 0); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
